import { useMemo } from 'react';

import { useGlobalConfig } from '../../common/utils/global-config/useGlobalConfig';

import { use3pConnectorNudgeMessage } from './use3pConnectorNudgeMessage';
import { useMaxSideNavBannerMessage } from './useMaxSideNavBannerMessage';

type PlacementMessage = Record<string, unknown>;
type MessageEligibility = Record<string, boolean | ((message: PlacementMessage) => boolean)>;

type RovoChatInputPlacementProps = {
	productSource: string;
	messageEligibility: MessageEligibility;
	connectorNudgeMessageProps: ReturnType<
		typeof use3pConnectorNudgeMessage
	>['connectorNudgeMessageProps'];
	maxSideNavBannerMessageProps: ReturnType<
		typeof useMaxSideNavBannerMessage
	>['maxSideNavBannerMessageProps'];
};

export const useRovoChatInputPlacementProps = (): RovoChatInputPlacementProps => {
	const { productKey } = useGlobalConfig();
	// To add a new message: create a hook returning { templateId, isEligible, ...messageProps }.
	// - isEligible is optional but should be referentially stable (useCallback) to avoid unnecessary downstream updates.
	// - Destructure with an alias (e.g. nudgeIsEligible) so multiple hooks can coexist.
	// - Add [aliasedTemplateId]: aliasedIsEligible to messageEligibility.
	// - Add message-specific props to placementProps under their own namespace.
	const {
		templateId: nudgeTemplateId,
		isEligible: nudgeIsEligible,
		connectorNudgeMessageProps,
	} = use3pConnectorNudgeMessage();
	const {
		templateId: maxTemplateId,
		fullPageTemplateId: maxFullPageTemplateId,
		isEligible: maxIsEligible,
		maxSideNavBannerMessageProps,
	} = useMaxSideNavBannerMessage();

	const messageEligibility = useMemo(
		() => ({
			// Add new entries here: [newTemplateId]: newIsEligible
			[nudgeTemplateId]: nudgeIsEligible,
			[maxTemplateId]: maxIsEligible,
			[maxFullPageTemplateId]: maxIsEligible,
		}),
		[nudgeTemplateId, nudgeIsEligible, maxTemplateId, maxFullPageTemplateId, maxIsEligible],
	);

	const placementProps = useMemo(
		() => ({
			productSource: productKey,
			messageEligibility,
			// Add new message-specific props here under their own namespace
			connectorNudgeMessageProps,
			maxSideNavBannerMessageProps,
		}),
		[productKey, connectorNudgeMessageProps, maxSideNavBannerMessageProps, messageEligibility],
	);

	return placementProps;
};

export type { RovoChatInputPlacementProps };
